import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, FileText,
  GitBranch, GitCompare, GitMerge, Layers3, Lightbulb, MessageCircleWarning,
  Ruler, Scale, ShieldAlert, StickyNote, Target, Wrench,
} from "lucide-react";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Node modifiers lab.
// Question under test (docs/node-modifiers-lab-brief.md): should scope
// exceptions, caveats, alternative wordings, and edge objections be full graph
// nodes, or attached objects that appear on focus? Two renderings of the same
// college-value argument: "peers" (everything is a node) and "modifiers"
// (core skeleton + attached objects).
// ---------------------------------------------------------------------------

type Mode = "peers" | "modifiers";
type Focus = { kind: "node" | "edge"; id: string } | null;
type CoreRole = "evidence" | "criterion" | "inference" | "thesis";
type PeerRole = CoreRole | "scope" | "qualifier" | "claim" | "warrant";
type ModifierKind = "exception" | "caveat";
type ObjectionKind = "undercut" | "repair";
type Point = { x: number; y: number };

type Variant = {
  id: string;
  intent: "original" | "steelman" | "reframe";
  author: string;
  text: string;
  prefers: number;
  note: string;
};

type Modifier = {
  id: string;
  kind: ModifierKind;
  author: string;
  text: string;
  // docked card position when the parent node is focused
  x: number;
  y: number;
  w: number;
};

type CoreNode = {
  id: string;
  role: CoreRole;
  title: string;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  variants?: Variant[];
  modifiers?: Modifier[];
};

type Objection = {
  id: string;
  kind: ObjectionKind;
  title: string;
  text: string;
  x: number;
  y: number;
};

type SkeletonEdge = {
  id: string;
  source: string;
  target: string;
  role: "evidences" | "supports" | "qualifies";
  label: string;
  points: Point[];
  labelAt: Point;
  pipsAt?: Point;
  objections?: Objection[];
};

type PeerNode = {
  id: string;
  role: PeerRole;
  title: string;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

type PeerEdge = {
  id: string;
  source: string;
  target: string;
  role: "evidences" | "supports" | "qualifies" | "reframes" | "undercuts" | "rebuts";
  points?: Point[]; // hand route; otherwise auto bezier
  diamondAtEnd?: boolean; // undercut hitting an edge, not a node
};

// ------------------------------------------------------------------ content

const THESIS_VARIANTS: Variant[] = [
  {
    id: "v-original",
    intent: "original",
    author: "mara_v",
    text: "College is not worth it for most people anymore.",
    prefers: 18,
    note: "The strain as first published.",
  },
  {
    id: "v-steelman",
    intent: "steelman",
    author: "tomas_r",
    text: "For students without a clear professional track, college is often not worth it once debt and opportunity cost are counted.",
    prefers: 31,
    note: "Narrows the population and concedes part of the premium is causal.",
  },
  {
    id: "v-reframe",
    intent: "reframe",
    author: "sora_k",
    text: "College should be an intentional bet on a specific track, not the default next step after high school.",
    prefers: 9,
    note: "Moves the claim from value to decision procedure.",
  },
];

const SKELETON_NODES: CoreNode[] = [
  {
    id: "ev-debt",
    role: "evidence",
    title: "Debt & completion",
    text: "About four in ten students who start college have not finished six years later, and typical borrowers graduate owing $20–35k.",
    x: 48, y: 120, w: 272, h: 124,
  },
  {
    id: "ev-premium",
    role: "evidence",
    title: "Earnings premium",
    text: "College graduates earn roughly $1M more over a lifetime than non-graduates on average.",
    x: 48, y: 318, w: 272, h: 142,
    modifiers: [
      {
        id: "m-premium-average",
        kind: "caveat",
        author: "dr_okafor",
        text: "That premium is an average. It hides large differences by major, region, and whether students actually finish.",
        x: 340, y: 470, w: 264,
      },
    ],
  },
  {
    id: "crit-worth",
    role: "criterion",
    title: "Decision rule",
    text: "“Worth it” here means a positive private financial return after debt, forgone wages, and completion risk.",
    x: 48, y: 536, w: 272, h: 148,
    modifiers: [
      {
        id: "m-crit-nonfinancial",
        kind: "caveat",
        author: "mara_v",
        text: "Non-financial value — networks, civic effects, personal growth — is deliberately out of scope for this strain.",
        x: 340, y: 620, w: 264,
      },
    ],
  },
  {
    id: "inf-roi",
    role: "inference",
    title: "Return on investment",
    text: "The expected return for a typical student depends on completion odds, major, and debt load — the average premium overstates the typical case.",
    x: 468, y: 300, w: 292, h: 150,
  },
  {
    id: "thesis",
    role: "thesis",
    title: "Conclusion",
    text: THESIS_VARIANTS[0].text,
    x: 968, y: 252, w: 336, h: 208,
    variants: THESIS_VARIANTS,
    modifiers: [
      {
        id: "m-licensed",
        kind: "exception",
        author: "leila_h",
        text: "Licensed professions — medicine, law, nursing, teaching — are clear exceptions, because the degree is legally required.",
        x: 992, y: 524, w: 304,
      },
    ],
  },
];

const SKELETON_EDGES: SkeletonEdge[] = [
  {
    id: "e-debt-roi",
    source: "ev-debt",
    target: "inf-roi",
    role: "evidences",
    label: "cost side",
    points: [{ x: 320, y: 182 }, { x: 396, y: 182 }, { x: 396, y: 340 }, { x: 468, y: 340 }],
    labelAt: { x: 396, y: 164 },
  },
  {
    id: "e-premium-roi",
    source: "ev-premium",
    target: "inf-roi",
    role: "evidences",
    label: "benefit side",
    points: [{ x: 320, y: 380 }, { x: 394, y: 380 }, { x: 394, y: 382 }, { x: 468, y: 382 }],
    labelAt: { x: 394, y: 358 },
    pipsAt: { x: 410, y: 381 },
    objections: [
      {
        id: "o-selection",
        kind: "undercut",
        title: "Selection, not schooling",
        text: "Part of the premium reflects who enrolls, not what college adds. Stronger students would out-earn either way.",
        x: 336, y: 108,
      },
      {
        id: "o-causal",
        kind: "repair",
        title: "A causal core survives",
        text: "Twin and admissions-cutoff studies still find a meaningful causal earnings effect, especially for licensed tracks.",
        x: 636, y: 108,
      },
    ],
  },
  {
    id: "e-roi-thesis",
    source: "inf-roi",
    target: "thesis",
    role: "supports",
    label: "net return is weak",
    points: [{ x: 760, y: 372 }, { x: 862, y: 372 }, { x: 862, y: 330 }, { x: 968, y: 330 }],
    labelAt: { x: 862, y: 404 },
  },
  {
    id: "e-crit-thesis",
    source: "crit-worth",
    target: "thesis",
    role: "qualifies",
    label: "sets the bar",
    points: [{ x: 320, y: 600 }, { x: 880, y: 600 }, { x: 880, y: 408 }, { x: 968, y: 408 }],
    labelAt: { x: 600, y: 582 },
  },
];

const PEER_NODES: PeerNode[] = [
  { id: "ev-debt", role: "evidence", title: "Debt & completion", text: "About four in ten students who start college have not finished six years later, and typical borrowers graduate owing $20–35k.", x: 48, y: 110, w: 270, h: 124 },
  { id: "ev-premium", role: "evidence", title: "Earnings premium", text: "College graduates earn roughly $1M more over a lifetime than non-graduates on average.", x: 48, y: 300, w: 270, h: 112 },
  { id: "p-caveat-premium", role: "qualifier", title: "Averages mislead", text: "The premium is an average; it hides differences by major, region, and completion.", x: 48, y: 480, w: 270, h: 116 },
  { id: "crit-worth", role: "criterion", title: "Decision rule", text: "“Worth it” means a positive private financial return after debt, forgone wages, and completion risk.", x: 48, y: 668, w: 270, h: 132 },
  { id: "p-selection", role: "warrant", title: "Selection effect", text: "Part of the premium reflects who enrolls, not what college adds. Stronger students would out-earn either way.", x: 470, y: 92, w: 280, h: 122 },
  { id: "inf-roi", role: "inference", title: "Return on investment", text: "Expected return for a typical student depends on completion odds, major, and debt — the average premium overstates the typical case.", x: 470, y: 300, w: 290, h: 146 },
  { id: "p-steelman", role: "claim", title: "Steelman thesis", text: "For students without a clear professional track, college is often not worth it once debt and opportunity cost are counted.", x: 470, y: 520, w: 300, h: 132 },
  { id: "p-caveat-crit", role: "qualifier", title: "Financial only", text: "Non-financial value — networks, civic effects, personal growth — is out of scope here.", x: 470, y: 716, w: 280, h: 116 },
  { id: "p-repair", role: "claim", title: "Causal core", text: "Twin and admissions-cutoff studies still find a meaningful causal earnings effect, especially for licensed tracks.", x: 836, y: 92, w: 280, h: 122 },
  { id: "thesis", role: "thesis", title: "Conclusion", text: "College is not worth it for most people anymore.", x: 950, y: 300, w: 320, h: 158 },
  { id: "p-exception", role: "scope", title: "Licensed professions", text: "Medicine, law, nursing, and teaching are clear exceptions because the degree is legally required.", x: 950, y: 530, w: 310, h: 122 },
  { id: "p-reframe", role: "claim", title: "Reframed thesis", text: "College should be an intentional bet on a specific track, not the default next step.", x: 950, y: 712, w: 310, h: 112 },
];

const PEER_EDGES: PeerEdge[] = [
  { id: "pe-debt-roi", source: "ev-debt", target: "inf-roi", role: "evidences" },
  { id: "pe-premium-roi", source: "ev-premium", target: "inf-roi", role: "evidences" },
  { id: "pe-roi-thesis", source: "inf-roi", target: "thesis", role: "supports" },
  { id: "pe-crit-thesis", source: "crit-worth", target: "thesis", role: "qualifies" },
  {
    id: "pe-caveat-premium",
    source: "p-caveat-premium",
    target: "ev-premium",
    role: "qualifies",
    points: [{ x: 318, y: 538 }, { x: 392, y: 538 }, { x: 392, y: 356 }, { x: 318, y: 356 }],
  },
  { id: "pe-caveat-crit", source: "p-caveat-crit", target: "crit-worth", role: "qualifies" },
  {
    id: "pe-selection-edge",
    source: "p-selection",
    target: "inf-roi",
    role: "undercuts",
    points: [{ x: 560, y: 214 }, { x: 400, y: 356 }],
    diamondAtEnd: true,
  },
  {
    id: "pe-repair-selection",
    source: "p-repair",
    target: "p-selection",
    role: "rebuts",
    points: [{ x: 836, y: 153 }, { x: 750, y: 153 }],
  },
  { id: "pe-steelman-thesis", source: "p-steelman", target: "thesis", role: "reframes" },
  {
    id: "pe-exception-thesis",
    source: "p-exception",
    target: "thesis",
    role: "qualifies",
    points: [{ x: 1105, y: 530 }, { x: 1105, y: 458 }],
  },
  {
    id: "pe-reframe-thesis",
    source: "p-reframe",
    target: "thesis",
    role: "reframes",
    points: [{ x: 1260, y: 768 }, { x: 1312, y: 768 }, { x: 1312, y: 379 }, { x: 1270, y: 379 }],
  },
];

// ------------------------------------------------------------------ styling

const ROLE_META: Record<PeerRole, { label: string; icon: ReactNode; color: string; description: string }> = {
  evidence: { label: "Evidence", icon: <FileText size={13} />, color: "var(--color-evidence)", description: "An empirical starting point." },
  criterion: { label: "Criterion", icon: <Ruler size={13} />, color: "var(--color-define)", description: "The standard used to judge the conclusion." },
  inference: { label: "Inference", icon: <GitMerge size={13} />, color: "var(--color-support)", description: "A middle step that turns evidence into a stronger claim." },
  thesis: { label: "Thesis", icon: <Target size={13} />, color: "var(--color-primary)", description: "The conclusion the strands converge on." },
  scope: { label: "Scope", icon: <AlertTriangle size={13} />, color: "var(--color-qualify)", description: "A scope condition rendered as a full node." },
  qualifier: { label: "Qualifier", icon: <StickyNote size={13} />, color: "var(--color-qualify)", description: "A caveat rendered as a full node." },
  claim: { label: "Claim", icon: <Target size={13} />, color: "var(--color-primary)", description: "A competing wording rendered as a full node." },
  warrant: { label: "Warrant", icon: <MessageCircleWarning size={13} />, color: "var(--color-undercut)", description: "An objection to an inference rendered as a full node." },
};

const EDGE_META: Record<PeerEdge["role"], { color: string; width: number; dash?: string }> = {
  evidences: { color: "var(--color-evidence)", width: 2.2 },
  supports: { color: "var(--color-support)", width: 2.8 },
  qualifies: { color: "var(--color-qualify)", width: 2.2, dash: "7 5" },
  reframes: { color: "var(--color-reframe)", width: 2, dash: "2.5 5.5" },
  undercuts: { color: "var(--color-undercut)", width: 2, dash: "8 6" },
  rebuts: { color: "var(--color-rebut)", width: 2.2 },
};

const MODIFIER_META: Record<ModifierKind, { label: string; icon: ReactNode; color: string; description: string }> = {
  exception: { label: "Exception", icon: <AlertTriangle size={11} />, color: "var(--color-qualify)", description: "Carves a population or context out of the claim without weakening the main chain." },
  caveat: { label: "Caveat", icon: <StickyNote size={11} />, color: "var(--color-ink-muted)", description: "A footnote-grade limitation: important to know, not load-bearing." },
};

const OBJECTION_META: Record<ObjectionKind, { label: string; icon: ReactNode; color: string }> = {
  undercut: { label: "Undercut", icon: <MessageCircleWarning size={12} />, color: "var(--color-undercut)" },
  repair: { label: "Repair", icon: <Wrench size={12} />, color: "var(--color-primary-bright)" },
};

const INTENT_META: Record<Variant["intent"], { label: string; color: string }> = {
  original: { label: "original", color: "var(--color-ink-muted)" },
  steelman: { label: "steelman", color: "var(--color-primary)" },
  reframe: { label: "reframe", color: "var(--color-reframe)" },
};

// ------------------------------------------------------------------ geometry

function roundedPolylinePath(points: Point[], radius = 14) {
  if (points.length < 2) return "";
  const [start, ...rest] = points;
  let path = `M ${start.x} ${start.y}`;
  rest.forEach((point, index) => {
    const next = rest[index + 1];
    const prev = points[index];
    if (!next) {
      path += ` L ${point.x} ${point.y}`;
      return;
    }
    const prevDistance = Math.hypot(point.x - prev.x, point.y - prev.y);
    const nextDistance = Math.hypot(next.x - point.x, next.y - point.y);
    const cornerRadius = Math.min(radius, prevDistance / 2, nextDistance / 2);
    if (cornerRadius <= 0) {
      path += ` L ${point.x} ${point.y}`;
      return;
    }
    const before = {
      x: point.x + ((prev.x - point.x) / prevDistance) * cornerRadius,
      y: point.y + ((prev.y - point.y) / prevDistance) * cornerRadius,
    };
    const after = {
      x: point.x + ((next.x - point.x) / nextDistance) * cornerRadius,
      y: point.y + ((next.y - point.y) / nextDistance) * cornerRadius,
    };
    path += ` L ${before.x} ${before.y} Q ${point.x} ${point.y} ${after.x} ${after.y}`;
  });
  return path;
}

function autoBezier(source: PeerNode, target: PeerNode) {
  const sc = { x: source.x + source.w / 2, y: source.y + source.h / 2 };
  const tc = { x: target.x + target.w / 2, y: target.y + target.h / 2 };
  const leftToRight = sc.x <= tc.x;
  const sx = leftToRight ? source.x + source.w : source.x;
  const tx = leftToRight ? target.x : target.x + target.w;
  const sy = sc.y;
  const ty = tc.y;
  const dx = Math.max(120, Math.abs(tx - sx) * 0.48);
  return {
    path: `M ${sx} ${sy} C ${sx + (leftToRight ? dx : -dx)} ${sy}, ${tx - (leftToRight ? dx : -dx)} ${ty}, ${tx} ${ty}`,
    start: { x: sx, y: sy },
    end: { x: tx, y: ty },
  };
}

// word-level diff (LCS) for comparing variant wordings in place
type DiffToken = { text: string; kind: "same" | "added" | "removed" };

function wordDiff(a: string, b: string): DiffToken[] {
  const aw = a.split(/\s+/);
  const bw = b.split(/\s+/);
  const m = aw.length;
  const n = bw.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      dp[i][j] = aw[i] === bw[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (aw[i] === bw[j]) {
      out.push({ text: aw[i], kind: "same" });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ text: aw[i], kind: "removed" });
      i += 1;
    } else {
      out.push({ text: bw[j], kind: "added" });
      j += 1;
    }
  }
  while (i < m) { out.push({ text: aw[i], kind: "removed" }); i += 1; }
  while (j < n) { out.push({ text: bw[j], kind: "added" }); j += 1; }
  return out;
}

// ------------------------------------------------------------------ component

const SKELETON_W = 1360;
const SKELETON_H = 740;
const PEERS_W = 1360;
const PEERS_H = 880;

export default function NodeModifiersLab() {
  const [mode, setMode] = useState<Mode>("modifiers");
  const [focus, setFocus] = useState<Focus>({ kind: "node", id: "thesis" });
  const [variantIndex, setVariantIndex] = useState<Record<string, number>>({});
  const [showDiff, setShowDiff] = useState(false);

  const skeletonById = useMemo(() => new Map(SKELETON_NODES.map((n) => [n.id, n])), []);
  const peerById = useMemo(() => new Map(PEER_NODES.map((n) => [n.id, n])), []);

  const focusNode = (id: string) => setFocus({ kind: "node", id });
  const focusEdge = (id: string) => setFocus({ kind: "edge", id });
  const clearFocus = () => { setFocus(null); setShowDiff(false); };

  const relatedIds = useMemo(() => {
    const ids = new Set<string>();
    if (!focus) return ids;
    const edges = mode === "modifiers" ? SKELETON_EDGES : PEER_EDGES;
    if (focus.kind === "node") {
      ids.add(focus.id);
      edges.forEach((e) => {
        if (e.source === focus.id || e.target === focus.id) {
          ids.add(e.source);
          ids.add(e.target);
        }
      });
    } else {
      const edge = edges.find((e) => e.id === focus.id);
      if (edge) {
        ids.add(edge.source);
        ids.add(edge.target);
      }
    }
    return ids;
  }, [focus, mode]);

  const focusedSkeletonNode = focus?.kind === "node" ? skeletonById.get(focus.id) : undefined;
  const focusedSkeletonEdge = focus?.kind === "edge" ? SKELETON_EDGES.find((e) => e.id === focus.id) : undefined;
  const focusedPeerNode = focus?.kind === "node" ? peerById.get(focus.id) : undefined;

  const setVariant = (nodeId: string, index: number) => {
    setVariantIndex((cur) => ({ ...cur, [nodeId]: index }));
  };

  const attachedCount = SKELETON_NODES.reduce(
    (sum, n) => sum + (n.modifiers?.length ?? 0) + Math.max(0, (n.variants?.length ?? 1) - 1),
    0,
  ) + SKELETON_EDGES.reduce((sum, e) => sum + (e.objections?.length ?? 0), 0);

  return (
    <div className="min-h-screen bg-strain-field text-[var(--color-ink)]">
      <header className="border-b border-[var(--color-border)] bg-[var(--color-surface)]/76 backdrop-blur">
        <div className="mx-auto max-w-[1500px] px-4 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="mb-3 flex items-center gap-3 text-[12px] text-[var(--color-ink-muted)]">
                <Link to="/app" className="inline-flex items-center gap-1.5 hover:text-[var(--color-ink)]">
                  <ArrowLeft size={13} /> Back to app
                </Link>
                <span className="text-[var(--color-ink-faint)]">·</span>
                <Link to="/lab/visual-language" className="hover:text-[var(--color-ink)]">
                  Foundations-to-thesis lab
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-lg border border-[var(--color-qualify)]/30 bg-[var(--color-qualify)]/10 text-[var(--color-qualify)]">
                  <Layers3 size={17} />
                </span>
                <div>
                  <h1 className="text-xl font-semibold tracking-tight">Node modifiers lab</h1>
                  <p className="text-[13px] text-[var(--color-ink-muted)]">
                    Same college-value argument, two visual grammars. Which one earns a full node?
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <ModeButton
                active={mode === "peers"}
                onClick={() => { setMode("peers"); setFocus(null); setShowDiff(false); }}
                label="Everything is a node"
                sub="12 cards · 11 links"
              />
              <ModeButton
                active={mode === "modifiers"}
                onClick={() => { setMode("modifiers"); setFocus(null); setShowDiff(false); }}
                label="Modifiers & variants"
                sub={`5 cards · 4 links · ${attachedCount} attached`}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1980px] gap-4 px-4 py-4 min-[1900px]:grid-cols-[minmax(0,1fr)_380px]">
        <section className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Prototype strain</div>
              <div className="text-[14px] font-semibold">Is college worth it for most people anymore?</div>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">
              {mode === "modifiers"
                ? "click a card to open its attachments · click the link pips for objections"
                : "the pre-modifier rendering: every object is a peer node"}
            </div>
          </div>

          <div className="overflow-auto bg-grid" onClick={clearFocus}>
            {mode === "modifiers" ? (
              <SkeletonCanvas
                focus={focus}
                relatedIds={relatedIds}
                variantIndex={variantIndex}
                showDiff={showDiff}
                onFocusNode={focusNode}
                onFocusEdge={focusEdge}
                onSetVariant={setVariant}
                onToggleDiff={() => setShowDiff((v) => !v)}
              />
            ) : (
              <PeersCanvas focus={focus} relatedIds={relatedIds} onFocusNode={focusNode} onFocusEdge={focusEdge} />
            )}
          </div>
        </section>

        <aside className="space-y-4">
          <Panel title="The test case" icon={<AlertTriangle size={14} />}>
            <p className="mb-3 text-[12px] leading-relaxed text-[var(--color-ink-soft)]">
              “Licensed professions are clear exceptions.” Important, true — and not a step in the
              reasoning chain. Compare its two lives:
            </p>
            <div className="grid grid-cols-2 gap-2">
              <TestCaseButton
                active={mode === "modifiers"}
                label="As a docked exception"
                sub="chip on the thesis, card on focus"
                onClick={() => { setMode("modifiers"); setFocus({ kind: "node", id: "thesis" }); }}
              />
              <TestCaseButton
                active={mode === "peers"}
                label="As a peer node"
                sub="full card + edge, always visible"
                onClick={() => { setMode("peers"); setFocus({ kind: "node", id: "p-exception" }); setShowDiff(false); }}
              />
            </div>
          </Panel>

          <Panel title="Current focus" icon={<Target size={14} />}>
            {mode === "modifiers" && focusedSkeletonNode ? (
              <FocusNodeDetail
                node={focusedSkeletonNode}
                variantIdx={variantIndex[focusedSkeletonNode.id] ?? 0}
                onSetVariant={(i) => setVariant(focusedSkeletonNode.id, i)}
              />
            ) : mode === "modifiers" && focusedSkeletonEdge ? (
              <FocusEdgeDetail edge={focusedSkeletonEdge} nodeById={skeletonById} />
            ) : mode === "peers" && focusedPeerNode ? (
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
                <div className="mb-1 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider" style={{ color: ROLE_META[focusedPeerNode.role].color }}>
                  {ROLE_META[focusedPeerNode.role].icon} {ROLE_META[focusedPeerNode.role].label}
                </div>
                <div className="text-[13px] font-medium leading-snug">{focusedPeerNode.text}</div>
                <div className="mt-2 text-[12px] leading-relaxed text-[var(--color-ink-muted)]">{ROLE_META[focusedPeerNode.role].description}</div>
              </div>
            ) : (
              <p className="text-[12px] leading-relaxed text-[var(--color-ink-muted)]">
                Nothing focused. Click a card or a link.
              </p>
            )}
          </Panel>

          <Panel title="The grammar" icon={<Lightbulb size={14} />}>
            <GrammarRow
              icon={<Target size={13} />}
              color="var(--color-primary)"
              label="Core node"
              text="Evidence, inferences, criteria, conclusions. Always visible. The only objects that get full cards by default."
            />
            <GrammarRow
              icon={<GitBranch size={13} />}
              color="var(--color-primary)"
              label="Variant"
              text="A competing wording of the same slot. Browsable in place via the version rail — the graph never changes shape."
            />
            <GrammarRow
              icon={<AlertTriangle size={13} />}
              color="var(--color-qualify)"
              label="Modifier"
              text="Exceptions and caveats docked to their node. A chip by default, a card on focus."
            />
            <GrammarRow
              icon={<ShieldAlert size={13} />}
              color="var(--color-undercut)"
              label="Edge objection"
              text="Undercuts and repairs live on the inference they attack, marked by pips until inspected."
            />
          </Panel>

          <Panel title="What to evaluate" icon={<Scale size={14} />}>
            <EvalRow n="1" text="Can you read the main chain in under ten seconds in each mode?" />
            <EvalRow n="2" text="In peers mode, does the licensed-professions card read as evidence at first glance?" />
            <EvalRow n="3" text="Does the version rail make the steelman feel like the same idea, or a different strain?" />
            <EvalRow n="4" text="Are chips enough of a scent that variants and exceptions exist without opening them?" />
          </Panel>
        </aside>
      </main>
    </div>
  );
}

// ------------------------------------------------------------------ canvases

function SkeletonCanvas({
  focus, relatedIds, variantIndex, showDiff, onFocusNode, onFocusEdge, onSetVariant, onToggleDiff,
}: {
  focus: Focus;
  relatedIds: Set<string>;
  variantIndex: Record<string, number>;
  showDiff: boolean;
  onFocusNode: (id: string) => void;
  onFocusEdge: (id: string) => void;
  onSetVariant: (nodeId: string, index: number) => void;
  onToggleDiff: () => void;
}) {
  const focusActive = focus !== null;
  const focusedNode = focus?.kind === "node" ? SKELETON_NODES.find((n) => n.id === focus.id) : undefined;
  const focusedEdge = focus?.kind === "edge" ? SKELETON_EDGES.find((e) => e.id === focus.id) : undefined;

  return (
    <div className="relative shrink-0" style={{ width: SKELETON_W, height: SKELETON_H }}>
      <ColumnLabel x={62} label="Foundations" />
      <ColumnLabel x={488} label="Inference" />
      <ColumnLabel x={988} label="Conclusion" />

      <svg className="pointer-events-none absolute inset-0 z-10 h-full w-full" viewBox={`0 0 ${SKELETON_W} ${SKELETON_H}`}>
        <defs>
          {(["evidences", "supports", "qualifies"] as const).map((role) => (
            <marker key={role} id={`nm-arrow-${role}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={EDGE_META[role].color} />
            </marker>
          ))}
        </defs>

        {SKELETON_EDGES.map((edge) => {
          const meta = EDGE_META[edge.role];
          const selected = focus?.kind === "edge" && focus.id === edge.id;
          const dimmed = focusActive && !selected && !(focus?.kind === "node" && (edge.source === focus.id || edge.target === focus.id));
          return (
            <g key={edge.id} opacity={dimmed ? 0.18 : 1} style={{ transition: "opacity 0.2s" }}>
              <path
                d={roundedPolylinePath(edge.points)}
                stroke="transparent"
                strokeWidth={18}
                fill="none"
                className="pointer-events-auto cursor-pointer"
                onClick={(event) => { event.stopPropagation(); onFocusEdge(edge.id); }}
              />
              <path
                d={roundedPolylinePath(edge.points)}
                stroke={meta.color}
                strokeWidth={selected ? meta.width + 1.4 : meta.width}
                strokeDasharray={meta.dash}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                markerEnd={`url(#nm-arrow-${edge.role})`}
                className="pointer-events-auto cursor-pointer"
                onClick={(event) => { event.stopPropagation(); onFocusEdge(edge.id); }}
              />
              <circle cx={edge.points[0].x} cy={edge.points[0].y} r={selected ? 4.3 : 3.5} fill="var(--color-surface)" stroke={meta.color} strokeWidth={selected ? 2.1 : 1.6} />
            </g>
          );
        })}

        {/* connectors from focused node to its docked modifier cards */}
        {focusedNode?.modifiers?.map((modifier) => {
          const fromX = focusedNode.x + Math.min(focusedNode.w * 0.35, 96);
          const fromY = focusedNode.y + focusedNode.h;
          const toX = modifier.x + 20;
          const toY = modifier.y;
          return (
            <g key={`stub-${modifier.id}`} className="animate-modifier-in">
              <path
                d={`M ${fromX} ${fromY} C ${fromX} ${fromY + 18}, ${toX} ${toY - 18}, ${toX} ${toY}`}
                stroke={MODIFIER_META[modifier.kind].color}
                strokeWidth={1.5}
                strokeDasharray="3 4"
                fill="none"
              />
              <circle cx={fromX} cy={fromY} r={3} fill={MODIFIER_META[modifier.kind].color} />
            </g>
          );
        })}
      </svg>

      {/* edge labels + objection pips */}
      {SKELETON_EDGES.map((edge) => {
        const selected = focus?.kind === "edge" && focus.id === edge.id;
        const touchesFocusedNode = focus?.kind === "node" && (edge.source === focus.id || edge.target === focus.id);
        const dimmed = focusActive && !selected && !touchesFocusedNode;
        return (
          <div key={`label-${edge.id}`} className={cn("absolute z-20 transition-opacity duration-200", dimmed && "opacity-20")}>
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); onFocusEdge(edge.id); }}
              className={cn(
                "absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border bg-[var(--color-surface)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider shadow-card transition-colors",
                selected ? "border-[var(--color-primary)] text-[var(--color-ink)]" : "border-[var(--color-border)]",
              )}
              style={{ left: edge.labelAt.x, top: edge.labelAt.y, color: selected ? undefined : EDGE_META[edge.role].color }}
            >
              {edge.label}
            </button>
            {edge.pipsAt && (edge.objections?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onFocusEdge(edge.id); }}
                title={`${edge.objections!.length} objections on this inference`}
                className={cn(
                  "absolute flex h-5 -translate-x-1/2 translate-y-1 items-center gap-0.5 rounded-full border bg-[var(--color-surface)] px-1.5 shadow-card transition-colors",
                  selected ? "border-[var(--color-primary)]" : "border-[var(--color-undercut)] hover:bg-[color-mix(in_srgb,var(--color-undercut)_12%,var(--color-surface)_88%)]",
                )}
                style={{ left: edge.pipsAt.x, top: edge.pipsAt.y }}
              >
                {edge.objections!.map((objection) => (
                  <span key={objection.id} className="h-1.5 w-1.5 rounded-full" style={{ background: OBJECTION_META[objection.kind].color }} />
                ))}
              </button>
            )}
          </div>
        );
      })}

      {/* core cards */}
      {SKELETON_NODES.map((node) => {
        const selected = focus?.kind === "node" && focus.id === node.id;
        const dimmed = focusActive && !relatedIds.has(node.id);
        return (
          <CoreCard
            key={node.id}
            node={node}
            selected={selected}
            dimmed={dimmed}
            variantIdx={variantIndex[node.id] ?? 0}
            showDiff={selected && showDiff}
            onClick={() => onFocusNode(node.id)}
            onSetVariant={(i) => onSetVariant(node.id, i)}
            onToggleDiff={onToggleDiff}
          />
        );
      })}

      {/* docked modifiers for the focused node */}
      {focusedNode?.modifiers?.map((modifier, index) => (
        <ModifierCard key={modifier.id} modifier={modifier} delayMs={60 * index} />
      ))}

      {/* objections for the focused edge */}
      {focusedEdge?.objections?.map((objection, index) => (
        <ObjectionCard key={objection.id} objection={objection} delayMs={60 * index} />
      ))}
    </div>
  );
}

function PeersCanvas({
  focus, relatedIds, onFocusNode, onFocusEdge,
}: {
  focus: Focus;
  relatedIds: Set<string>;
  onFocusNode: (id: string) => void;
  onFocusEdge: (id: string) => void;
}) {
  const nodeById = useMemo(() => new Map(PEER_NODES.map((n) => [n.id, n])), []);
  const focusActive = focus !== null;

  return (
    <div className="relative shrink-0" style={{ width: PEERS_W, height: PEERS_H }}>
      <svg className="pointer-events-none absolute inset-0 z-10 h-full w-full" viewBox={`0 0 ${PEERS_W} ${PEERS_H}`}>
        <defs>
          {(Object.keys(EDGE_META) as PeerEdge["role"][]).map((role) => (
            <marker key={role} id={`nm-peer-arrow-${role}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={EDGE_META[role].color} />
            </marker>
          ))}
        </defs>
        {PEER_EDGES.map((edge) => {
          const source = nodeById.get(edge.source);
          const target = nodeById.get(edge.target);
          if (!source || !target) return null;
          const meta = EDGE_META[edge.role];
          const selected = focus?.kind === "edge" && focus.id === edge.id;
          const dimmed = focusActive && !selected && !(focus?.kind === "node" && (edge.source === focus.id || edge.target === focus.id));
          const geometry = edge.points
            ? { path: roundedPolylinePath(edge.points), start: edge.points[0], end: edge.points[edge.points.length - 1] }
            : autoBezier(source, target);
          return (
            <g key={edge.id} opacity={dimmed ? 0.15 : 1} style={{ transition: "opacity 0.2s" }}>
              <path
                d={geometry.path}
                stroke="transparent"
                strokeWidth={16}
                fill="none"
                className="pointer-events-auto cursor-pointer"
                onClick={(event) => { event.stopPropagation(); onFocusEdge(edge.id); }}
              />
              <path
                d={geometry.path}
                stroke={meta.color}
                strokeWidth={selected ? meta.width + 1.2 : meta.width}
                strokeDasharray={meta.dash}
                fill="none"
                strokeLinecap="round"
                markerEnd={edge.diamondAtEnd ? undefined : `url(#nm-peer-arrow-${edge.role})`}
                className="pointer-events-auto cursor-pointer"
                onClick={(event) => { event.stopPropagation(); onFocusEdge(edge.id); }}
              />
              {edge.diamondAtEnd && (
                <rect
                  x={geometry.end.x - 5}
                  y={geometry.end.y - 5}
                  width={10}
                  height={10}
                  transform={`rotate(45 ${geometry.end.x} ${geometry.end.y})`}
                  fill={meta.color}
                  stroke="var(--color-base)"
                />
              )}
            </g>
          );
        })}
      </svg>

      {PEER_NODES.map((node) => {
        const meta = ROLE_META[node.role];
        const selected = focus?.kind === "node" && focus.id === node.id;
        const dimmed = focusActive && !relatedIds.has(node.id);
        const isThesis = node.role === "thesis";
        return (
          <button
            key={node.id}
            type="button"
            onClick={(event) => { event.stopPropagation(); onFocusNode(node.id); }}
            style={{ left: node.x, top: node.y, width: node.w, minHeight: node.h } satisfies CSSProperties}
            className={cn(
              "absolute z-20 text-left transition-all duration-200",
              selected && "z-40 scale-[1.015]",
              dimmed && "opacity-25",
            )}
          >
            <div
              className={cn(
                "h-full rounded-lg border bg-[var(--color-surface)] p-3 shadow-card",
                isThesis && "rounded-xl border-[var(--color-primary)]/75 bg-[color-mix(in_srgb,var(--color-primary)_11%,var(--color-surface)_89%)] shadow-glow-primary",
                selected && "ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-base)]",
              )}
              style={!isThesis ? { borderColor: `color-mix(in srgb, ${meta.color} 46%, var(--color-border) 54%)` } : undefined}
            >
              <div className="mb-1.5 flex items-center gap-1.5">
                <span
                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
                  style={{
                    color: meta.color,
                    borderColor: `color-mix(in srgb, ${meta.color} 44%, var(--color-border) 56%)`,
                    background: `color-mix(in srgb, ${meta.color} 10%, var(--color-surface) 90%)`,
                  }}
                >
                  {meta.icon}{meta.label}
                </span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[var(--color-ink-faint)]">{node.title}</span>
              </div>
              <div className={cn("leading-snug", isThesis ? "text-[14px] font-semibold" : "text-[13px] text-[var(--color-ink-soft)]")}>
                {node.text}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ cards

function CoreCard({
  node, selected, dimmed, variantIdx, showDiff, onClick, onSetVariant, onToggleDiff,
}: {
  node: CoreNode;
  selected: boolean;
  dimmed: boolean;
  variantIdx: number;
  showDiff: boolean;
  onClick: () => void;
  onSetVariant: (index: number) => void;
  onToggleDiff: () => void;
}) {
  const meta = ROLE_META[node.role];
  const isThesis = node.role === "thesis";
  const variants = node.variants;
  const variant = variants?.[variantIdx] ?? null;
  const viewingAlternate = !!variants && variantIdx !== 0;
  const text = variant?.text ?? node.text;
  const versionCount = variants?.length ?? 0;
  const modifierCount = node.modifiers?.length ?? 0;
  const exceptionCount = node.modifiers?.filter((m) => m.kind === "exception").length ?? 0;
  const caveatCount = modifierCount - exceptionCount;

  return (
    <button
      type="button"
      onClick={(event) => { event.stopPropagation(); onClick(); }}
      style={{ left: node.x, top: node.y, width: node.w, minHeight: node.h } satisfies CSSProperties}
      className={cn(
        "absolute z-20 text-left transition-all duration-200",
        selected && "z-40 scale-[1.01]",
        dimmed && "opacity-25",
      )}
    >
      <div
        className={cn(
          "relative h-full border bg-[var(--color-surface)] p-3 shadow-card",
          isThesis
            ? "rounded-xl border-[var(--color-primary)]/75 bg-[color-mix(in_srgb,var(--color-primary)_10%,var(--color-surface)_90%)] shadow-glow-primary"
            : node.role === "evidence"
            ? "rounded-md border-[var(--color-evidence)]/55"
            : node.role === "criterion"
            ? "rounded-lg border-[var(--color-define)]/60 bg-[color-mix(in_srgb,var(--color-define)_7%,var(--color-surface)_93%)]"
            : "rounded-xl border-[var(--color-support)]/45",
          viewingAlternate && "border-[var(--color-primary-bright)]",
          selected && "ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-base)]",
        )}
      >
        {node.role === "evidence" && (
          <span className="absolute right-0 top-0 h-5 w-5 border-b border-l border-[var(--color-evidence)]/50 bg-[var(--color-surface-2)]" />
        )}

        <div className="mb-1.5 flex items-center gap-1.5">
          <span
            className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
            style={{
              color: meta.color,
              borderColor: `color-mix(in srgb, ${meta.color} 44%, var(--color-border) 56%)`,
              background: `color-mix(in srgb, ${meta.color} 10%, var(--color-surface) 90%)`,
            }}
          >
            {meta.icon}{meta.label}
          </span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[var(--color-ink-faint)]">{node.title}</span>
        </div>

        {/* version rail — only when focused and the slot has variants */}
        {variants && selected && (
          <div
            className="mb-2 flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-1.5 py-1"
            onClick={(event) => event.stopPropagation()}
          >
            <RailButton
              disabled={variantIdx === 0}
              onClick={() => onSetVariant(Math.max(0, variantIdx - 1))}
              icon={<ChevronLeft size={13} />}
              label="Previous version"
            />
            <div className="flex items-center gap-1">
              {variants.map((item, index) => (
                <span
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSetVariant(index)}
                  onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onSetVariant(index); }}
                  title={`${INTENT_META[item.intent].label} · @${item.author}`}
                  className={cn(
                    "h-1.5 cursor-pointer rounded-full transition-all",
                    index === variantIdx ? "w-4" : "w-1.5 opacity-40 hover:opacity-80",
                  )}
                  style={{ background: INTENT_META[item.intent].color }}
                />
              ))}
            </div>
            <RailButton
              disabled={variantIdx === variants.length - 1}
              onClick={() => onSetVariant(Math.min(variants.length - 1, variantIdx + 1))}
              icon={<ChevronRight size={13} />}
              label="Next version"
            />
            <span className="ml-1 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider" style={{ color: INTENT_META[variant!.intent].color }}>
              {INTENT_META[variant!.intent].label}
              <span className="text-[var(--color-ink-faint)]">@{variant!.author}</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={onToggleDiff}
              onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onToggleDiff(); }}
              title="Compare with the original wording"
              className={cn(
                "ml-auto inline-flex h-5 cursor-pointer items-center gap-1 rounded border px-1.5 font-mono text-[9px] uppercase tracking-wider transition-colors",
                showDiff
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
                variantIdx === 0 && "pointer-events-none opacity-30",
              )}
            >
              <GitCompare size={10} /> diff
            </span>
          </div>
        )}

        <div key={variant?.id ?? "static"} className={cn("leading-snug animate-variant-swap", isThesis ? "text-[15px] font-semibold text-[var(--color-ink)]" : "text-[13px] text-[var(--color-ink-soft)]")}>
          {showDiff && variants && variantIdx !== 0 ? (
            <DiffText from={variants[0].text} to={text} />
          ) : (
            text
          )}
        </div>

        {variant && selected && (
          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--color-ink-faint)]">
            <span className="inline-flex items-center gap-1">
              <Scale size={10} /> {variant.prefers} prefer this wording
            </span>
            <span className="truncate">{variant.note}</span>
          </div>
        )}

        {/* attachment tray — the always-visible scent that more exists here */}
        {(versionCount > 1 || modifierCount > 0) && (
          <div className="mt-2 flex items-center gap-1.5 border-t border-[var(--color-border)]/60 pt-2">
            {versionCount > 1 && (
              <TrayChip
                icon={<GitBranch size={9} />}
                color="var(--color-primary)"
                label={`${versionCount} versions`}
                active={selected}
              />
            )}
            {exceptionCount > 0 && (
              <TrayChip
                icon={<AlertTriangle size={9} />}
                color="var(--color-qualify)"
                label={`${exceptionCount} exception${exceptionCount === 1 ? "" : "s"}`}
                active={selected}
              />
            )}
            {caveatCount > 0 && (
              <TrayChip
                icon={<StickyNote size={9} />}
                color="var(--color-ink-muted)"
                label={`${caveatCount} caveat${caveatCount === 1 ? "" : "s"}`}
                active={selected}
              />
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function ModifierCard({ modifier, delayMs }: { modifier: Modifier; delayMs: number }) {
  const meta = MODIFIER_META[modifier.kind];
  return (
    <div
      className="absolute z-30 animate-modifier-in"
      style={{ left: modifier.x, top: modifier.y, width: modifier.w, animationDelay: `${delayMs}ms` } satisfies CSSProperties}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="rounded-md border-l-2 bg-[var(--color-surface-2)] p-2.5 shadow-card"
        style={{
          borderLeftColor: meta.color,
          border: `1px solid color-mix(in srgb, ${meta.color} 32%, var(--color-border) 68%)`,
          borderLeft: `2px solid ${meta.color}`,
        }}
      >
        <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: meta.color }}>
          {meta.icon}
          <span>{meta.label}</span>
          <span className="ml-auto text-[var(--color-ink-faint)]">@{modifier.author}</span>
        </div>
        <div className="text-[12px] leading-[1.4] text-[var(--color-ink-soft)]">{modifier.text}</div>
      </div>
    </div>
  );
}

function ObjectionCard({ objection, delayMs }: { objection: Objection; delayMs: number }) {
  const meta = OBJECTION_META[objection.kind];
  return (
    <div
      className="absolute z-30 animate-modifier-in"
      style={{ left: objection.x, top: objection.y, width: 276, animationDelay: `${delayMs}ms` } satisfies CSSProperties}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="rounded-sm border bg-[var(--color-surface)] p-2.5 shadow-pop"
        style={{
          borderColor: `color-mix(in srgb, ${meta.color} 58%, var(--color-border) 42%)`,
          background: `color-mix(in srgb, ${meta.color} 9%, var(--color-surface) 91%)`,
        }}
      >
        <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: meta.color }}>
          {meta.icon}
          <span>{meta.label}</span>
          <span className="text-[var(--color-ink-faint)]">/ {objection.title}</span>
        </div>
        <div className="text-[12px] leading-[1.4] text-[var(--color-ink)]">{objection.text}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ side-panel details

function FocusNodeDetail({
  node, variantIdx, onSetVariant,
}: {
  node: CoreNode;
  variantIdx: number;
  onSetVariant: (index: number) => void;
}) {
  const meta = ROLE_META[node.role];
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
          {meta.icon} {meta.label}
        </div>
        <div className="text-[13px] font-medium leading-snug">{node.variants?.[variantIdx]?.text ?? node.text}</div>
      </div>

      {node.variants && (
        <div>
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">Versions of this slot</div>
          {node.variants.map((variant, index) => (
            <button
              key={variant.id}
              type="button"
              onClick={() => onSetVariant(index)}
              className={cn(
                "mb-1.5 w-full rounded-lg border p-2 text-left transition-colors last:mb-0",
                index === variantIdx
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8"
                  : "border-[var(--color-border)] bg-[var(--color-surface-2)] hover:border-[var(--color-primary)]/50",
              )}
            >
              <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider">
                <span style={{ color: INTENT_META[variant.intent].color }}>{INTENT_META[variant.intent].label}</span>
                <span className="text-[var(--color-ink-faint)]">@{variant.author}</span>
                <span className="ml-auto text-[var(--color-ink-faint)]">{variant.prefers} prefer</span>
              </div>
              <div className="mt-1 text-[12px] leading-snug text-[var(--color-ink-soft)]">{variant.text}</div>
            </button>
          ))}
        </div>
      )}

      {node.modifiers && node.modifiers.length > 0 && (
        <div>
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">Attached to this node</div>
          {node.modifiers.map((modifier) => (
            <div key={modifier.id} className="mb-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2 last:mb-0">
              <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: MODIFIER_META[modifier.kind].color }}>
                {MODIFIER_META[modifier.kind].icon} {MODIFIER_META[modifier.kind].label}
                <span className="ml-auto text-[var(--color-ink-faint)]">@{modifier.author}</span>
              </div>
              <div className="mt-1 text-[12px] leading-snug text-[var(--color-ink-soft)]">{modifier.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FocusEdgeDetail({ edge, nodeById }: { edge: SkeletonEdge; nodeById: Map<string, CoreNode> }) {
  const meta = EDGE_META[edge.role];
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
          <ArrowRight size={12} /> {edge.role} · {edge.label}
        </div>
        <div className="mb-1 text-[12px] leading-snug text-[var(--color-ink-soft)]">{nodeById.get(edge.source)?.text}</div>
        <div className="my-1 text-center text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">↓</div>
        <div className="text-[12px] leading-snug text-[var(--color-ink-soft)]">{nodeById.get(edge.target)?.text}</div>
      </div>
      {edge.objections && edge.objections.length > 0 && (
        <div>
          <div className="mb-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">On this inference</div>
          {edge.objections.map((objection) => (
            <div key={objection.id} className="mb-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2 last:mb-0">
              <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider" style={{ color: OBJECTION_META[objection.kind].color }}>
                {OBJECTION_META[objection.kind].icon} {OBJECTION_META[objection.kind].label} / {objection.title}
              </div>
              <div className="mt-1 text-[12px] leading-snug text-[var(--color-ink-soft)]">{objection.text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ atoms

function DiffText({ from, to }: { from: string; to: string }) {
  const tokens = useMemo(() => wordDiff(from, to), [from, to]);
  return (
    <span>
      {tokens.map((token, index) => (
        <span
          key={index}
          className={cn(
            token.kind === "added" && "rounded-sm bg-[color-mix(in_srgb,var(--color-support)_22%,transparent)] text-[var(--color-support)]",
            token.kind === "removed" && "rounded-sm bg-[color-mix(in_srgb,var(--color-rebut)_16%,transparent)] text-[var(--color-rebut)] line-through opacity-70",
          )}
        >
          {token.text}
          {index < tokens.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}

function RailButton({ disabled, onClick, icon, label }: { disabled: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-disabled={disabled}
      onClick={() => { if (!disabled) onClick(); }}
      onKeyDown={(event) => { if (!disabled && (event.key === "Enter" || event.key === " ")) onClick(); }}
      className={cn(
        "grid h-5 w-5 cursor-pointer place-items-center rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] transition-colors hover:text-[var(--color-ink)]",
        disabled && "pointer-events-none opacity-30",
      )}
    >
      {icon}
    </span>
  );
}

function TrayChip({ icon, color, label, active }: { icon: ReactNode; color: string; label: string; active: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center gap-1 rounded-full border px-1.5 font-mono text-[9px] uppercase tracking-wider transition-colors",
        active ? "opacity-100" : "opacity-80",
      )}
      style={{
        color,
        borderColor: `color-mix(in srgb, ${color} 38%, var(--color-border) 62%)`,
        background: `color-mix(in srgb, ${color} 8%, var(--color-surface) 92%)`,
      }}
    >
      {icon}{label}
    </span>
  );
}

function ModeButton({ active, onClick, label, sub }: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start rounded-lg border px-3 py-1.5 text-left transition-colors",
        active
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/12"
          : "border-[var(--color-border)] bg-[var(--color-surface-2)] hover:border-[var(--color-border-bright)]",
      )}
    >
      <span className={cn("text-[12px] font-semibold", active ? "text-[var(--color-primary-bright)]" : "text-[var(--color-ink-soft)]")}>{label}</span>
      <span className="font-mono text-[9px] uppercase tracking-wider text-[var(--color-ink-faint)]">{sub}</span>
    </button>
  );
}

function TestCaseButton({ active, label, sub, onClick }: { active: boolean; label: string; sub: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-lg border p-2.5 text-left transition-colors",
        active
          ? "border-[var(--color-qualify)] bg-[var(--color-qualify)]/10"
          : "border-[var(--color-border)] bg-[var(--color-surface-2)] hover:border-[var(--color-qualify)]/60",
      )}
    >
      <div className="text-[12px] font-semibold text-[var(--color-ink)]">{label}</div>
      <div className="mt-0.5 text-[10px] leading-snug text-[var(--color-ink-muted)]">{sub}</div>
    </button>
  );
}

function ColumnLabel({ x, label }: { x: number; label: string }) {
  return (
    <div
      className="pointer-events-none absolute top-7 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)]/90 px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]"
      style={{ left: x }}
    >
      {label}
    </div>
  );
}

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-card">
      <div className="mb-3 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">
        {icon}{title}
      </div>
      {children}
    </section>
  );
}

function GrammarRow({ icon, color, label, text }: { icon: ReactNode; color: string; label: string; text: string }) {
  return (
    <div className="mb-2.5 flex items-start gap-2 last:mb-0">
      <span className="mt-0.5" style={{ color }}>{icon}</span>
      <div>
        <div className="text-[12px] font-semibold">{label}</div>
        <div className="text-[11px] leading-relaxed text-[var(--color-ink-muted)]">{text}</div>
      </div>
    </div>
  );
}

function EvalRow({ n, text }: { n: string; text: string }) {
  return (
    <div className="mb-2 flex gap-2 last:mb-0">
      <span className="mt-0.5 h-5 shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-surface-2)] px-1.5 font-mono text-[9px] uppercase leading-5 tracking-wider text-[var(--color-qualify)]">
        {n}
      </span>
      <span className="text-[12px] leading-relaxed text-[var(--color-ink-soft)]">{text}</span>
    </div>
  );
}
